import { useState, useEffect } from 'react'
import { Analytics } from '@vercel/analytics/react'
import LandingPage from './components'
import PasswordProtection from './components/PasswordProtection'
import { TestRouter } from './debug/testing/TestRouter'
import CommandPalette from './components/CommandPalette'
import ChatApp from './components/chat/ChatApp'
import ZenSettingsModal from './components/ZenSettings'
import ZenCreatorSimple from './components/ZenCreatorSimple'
import { ZenCreator } from './components/ZenCreator'
import ZenCreatorSimpleMOCK from './components/ZenCreatorSimpleMOCK'
import ShareViewer from './components/ShareViewer'
import SharesAdmin from './pages/SharesAdmin'
import FeedbackAdmin from './pages/FeedbackAdmin'
import FeedbackButton from './components/feedback/FeedbackButton'
import DemoViewer from './components/DemoViewer'
import ConceptGraphExplorer from './pages/ConceptGraphExplorer'
import ImageLab from './pages/ImageLab'
import { useCommandPalette } from './hooks/useCommandPalette'
import { useCommands } from './hooks/useCommands'
import { useChat } from './hooks/useChat'
import { useProviders } from './hooks/useProviders'
import { useAdminMode } from './hooks/useAdminMode'
import { useStatusBarColor } from './hooks/useStatusBarColor'

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [testMode, setTestMode] = useState<string | null>(null)
  const [currentPage, setCurrentPage] = useState<string>('home')
  const [isChatOpen, setIsChatOpen] = useState(false)
  const [isSettingsOpen, setIsSettingsOpen] = useState(false)

  // Admin mode
  const isAdmin = useAdminMode()

  // Dynamic status bar color based on current page
  useStatusBarColor(currentPage)

  // Command palette and chat hooks
  const commandPalette = useCommandPalette()
  const { clearMessages } = useChat()
  const { refreshProviders } = useProviders()

  // Command registration
  const { commands } = useCommands({
    onOpenChat: () => setIsChatOpen(true),
    onClearChat: clearMessages,
    onRefreshProviders: refreshProviders,
    onOpenSettings: () => setIsSettingsOpen(true),
    isAdmin
  })

  useEffect(() => {
    // Check if user is already authenticated from previous session
    const authStatus = sessionStorage.getItem('syntaxlens_authenticated')

    // Skip authentication in development mode
    const isDevelopment = import.meta.env.DEV

    // PRODUCTION: Disable password protection - set to true to allow public access
    setIsAuthenticated(true)
    setIsLoading(false)

    // Check URL parameters for test mode and page navigation
    const path = window.location.pathname
    const urlParams = new URLSearchParams(window.location.search)
    const testParam = urlParams.get('test')
    const pageParam = urlParams.get('page')
    const shareParam = urlParams.get('share')
    setTestMode(testParam)

    // Handle share URLs - support both /snippet/:id and legacy ?share= formats
    let shareId = null
    if (path.startsWith('/snippet/')) {
      const pathParts = path.split('/')
      shareId = pathParts[2] // /snippet/[id]
    } else if (shareParam) {
      // Backward compatibility with old ?share= URLs
      shareId = shareParam
    }

    // Handle admin routes with path-based routing
    if (path === '/shares-admin' || path === '/admin-shares') {
      setCurrentPage('admin-shares')
    } else if (path === '/feedback-admin' || path === '/admin-feedback') {
      setCurrentPage('admin-feedback')
    } else if (shareId) {
      setCurrentPage(`share-${shareId}`)
    } else {
      setCurrentPage(pageParam || 'home')
    }

    // Add keyboard shortcuts for test mode
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.shiftKey && event.key === 'T') {
        // Ctrl+Shift+T: Toggle test mode (cycle through modes)
        const url = new URL(window.location.href)
        if (!testParam) {
          url.searchParams.set('test', 'highlight')
        } else {
          url.searchParams.delete('test')
        }
        window.location.href = url.toString()
      } else if (event.ctrlKey && event.shiftKey && event.key === 'H') {
        // Ctrl+Shift+H: Quick HighlightTester
        const url = new URL(window.location.href)
        url.searchParams.set('test', 'highlight')
        window.location.href = url.toString()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  const handleAuthentication = () => {
    setIsAuthenticated(true)
  }

  if (isLoading) {
    return (
      <div className="App">
        <div className="min-h-screen bg-white flex items-center justify-center">
          <div className="text-xl text-gray-600">Loading...</div>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return (
      <div className="App">
        <PasswordProtection onAuthenticated={handleAuthentication} />
      </div>
    )
  }

  // Show test app if in test mode
  if (testMode) {
    return (
      <div className="App">
        <TestRouter testMode={testMode} />
        <Analytics />
      </div>
    )
  }

  // Render page content based on current page
  const renderPageContent = () => {
    // Parse URL params for demo routes
    const urlParams = new URLSearchParams(window.location.search)

    // Handle share URLs
    if (currentPage.startsWith('share-')) {
      const shareId = currentPage.replace('share-', '')
      return <ShareViewer shareId={shareId} />
    }

    switch (currentPage) {
      case 'zen-simple':
        return <ZenCreatorSimple />
      case 'zen-creator':
        return <ZenCreator />
      case 'zen-mock':
        return <ZenCreatorSimpleMOCK />
      case 'admin-shares':
        return <SharesAdmin />
      case 'admin-feedback':
        return <FeedbackAdmin />
      case 'concept-graph':
        return <ConceptGraphExplorer />
      case 'image-lab':
        return <ImageLab />
      case 'demo':
        const demoId = urlParams.get('demo')
        return <DemoViewer demoId={demoId} />
      case 'home':
      default:
        return <LandingPage />
    }
  }

  return (
    <div className="App">
      {renderPageContent()}
      <FeedbackButton />
      <CommandPalette
        isOpen={commandPalette.isOpen}
        onClose={commandPalette.close}
        commands={commands}
      />
      <ChatApp
        isOpen={isChatOpen}
        onClose={() => setIsChatOpen(false)}
      />
      <ZenSettingsModal
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
      />
      <Analytics />
    </div>
  )
}

export default App
